import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNotifications } from '@carouselgg/nexus/notification-center'
import type {
  UseNotificationsReturn,
  Notification,
} from '@carouselgg/nexus/notification-center'
import { apiFetch } from '@carouselgg/auth/client'
import { useAuthStore } from '@carouselgg/auth/store'
import * as Ably from 'ably'

interface NotificationsPage {
  notifications: Notification[]
  hasMore: boolean
  total: number
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const NotificationContext = createContext<UseNotificationsReturn | null>(null)

export function NotificationProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [initialNotifications, setInitialNotifications] = useState<
    Notification[]
  >([])
  const [initialLoaded, setInitialLoaded] = useState(false)
  const ablyClientRef = useRef<Ably.Realtime | null>(null)
  const addNotificationRef = useRef<((n: Notification) => void) | null>(null)

  // Fetch initial notifications on auth
  useEffect(() => {
    if (!isAuthenticated) {
      setInitialNotifications([])
      setInitialLoaded(false)
      return
    }

    apiFetch<NotificationsPage>('/notifications?page=1&page_size=20')
      .then((data) => {
        setInitialNotifications(data.notifications)
        setInitialLoaded(true)
      })
      .catch(() => {
        setInitialNotifications([])
        setInitialLoaded(true)
      })
  }, [isAuthenticated])

  // Ably real-time subscription
  useEffect(() => {
    if (!isAuthenticated) return

    let channel: Ably.RealtimeChannel | null = null

    const connect = async () => {
      try {
        // Fetch once up front purely to learn the clientId for the channel name.
        const tokenData = await apiFetch<Ably.TokenRequest>(
          '/notifications/ably-token',
        )

        const client = new Ably.Realtime({
          // Ably re-invokes this whenever it needs to (re)authenticate — including
          // after the connection drops on an idle/sleeping tab. A TokenRequest's
          // timestamp + nonce are only valid for a couple of minutes, so we must
          // fetch a FRESH one each time. Replaying the initial request here is what
          // caused the overnight "40104 Timestamp not current" reconnect loop.
          authCallback: (_params, callback) => {
            apiFetch<Ably.TokenRequest>('/notifications/ably-token')
              .then((fresh) => callback(null, fresh))
              .catch((err: unknown) =>
                callback(
                  err instanceof Error ? err.message : 'ably token fetch failed',
                  null,
                ),
              )
          },
        })

        ablyClientRef.current = client
        channel = client.channels.get(`user:${tokenData.clientId}`)

        channel.subscribe('notification', (msg) => {
          const notification = msg.data as Notification
          if (typeof msg.data === 'string') {
            try {
              const parsed = JSON.parse(msg.data) as Notification
              addNotificationRef.current?.(parsed)
            } catch {
              // ignore malformed data
            }
          } else {
            addNotificationRef.current?.(notification)
          }
        })
      } catch {
        // Ably not configured or token fetch failed — degrade gracefully
      }
    }

    connect()

    return () => {
      channel?.unsubscribe()
      ablyClientRef.current?.close()
      ablyClientRef.current = null
    }
  }, [isAuthenticated])

  const fetchMore = useCallback(
    async (
      page: number,
    ): Promise<{ notifications: Notification[]; hasMore: boolean }> => {
      try {
        const data = await apiFetch<NotificationsPage>(
          `/notifications?page=${page + 1}&page_size=20`,
        )
        return { notifications: data.notifications, hasMore: data.hasMore }
      } catch {
        return { notifications: [], hasMore: false }
      }
    },
    [],
  )

  const value = useNotifications(initialNotifications, {
    fetchMore,
    loadDelay: 0,
  })

  // Keep a ref to the latest value for stable callbacks
  const valueRef = useRef(value)
  valueRef.current = value

  // Wire up real-time notification insertion
  useEffect(() => {
    addNotificationRef.current = (n: Notification) => {
      setInitialNotifications((prev) => [n, ...prev])
    }
  }, [])

  // Stable wrapped mutations that also fire API calls (fire-and-forget)
  const wrappedMarkRead = useCallback((id: string) => {
    valueRef.current.markRead(id)
    apiFetch(`/notifications/${id}/read`, { method: 'PATCH' }).catch(noop)
  }, [])

  const wrappedMarkAllRead = useCallback(() => {
    valueRef.current.markAllRead()
    apiFetch('/notifications/read-all', { method: 'POST' }).catch(noop)
  }, [])

  const wrappedDismiss = useCallback((id: string) => {
    valueRef.current.dismiss(id)
    apiFetch(`/notifications/${id}`, { method: 'DELETE' }).catch(noop)
  }, [])

  const wrappedDismissAll = useCallback(() => {
    valueRef.current.dismissAll()
    apiFetch('/notifications/dismiss-all', { method: 'POST' }).catch(noop)
  }, [])

  const wrappedClearRead = useCallback(() => {
    valueRef.current.clearRead()
    apiFetch('/notifications/clear-read', { method: 'POST' }).catch(noop)
  }, [])

  const wrappedUndoDismiss = useCallback((id: string) => {
    valueRef.current.undoDismiss(id)
    apiFetch(`/notifications/${id}/restore`, { method: 'PATCH' }).catch(noop)
  }, [])

  const wrappedValue: UseNotificationsReturn = useMemo(
    () => ({
      ...value,
      markRead: wrappedMarkRead,
      markAllRead: wrappedMarkAllRead,
      dismiss: wrappedDismiss,
      dismissAll: wrappedDismissAll,
      clearRead: wrappedClearRead,
      undoDismiss: wrappedUndoDismiss,
    }),
    [
      value,
      wrappedMarkRead,
      wrappedMarkAllRead,
      wrappedDismiss,
      wrappedDismissAll,
      wrappedClearRead,
      wrappedUndoDismiss,
    ],
  )

  if (!initialLoaded && isAuthenticated) {
    return (
      <NotificationContext.Provider value={wrappedValue}>
        {children}
      </NotificationContext.Provider>
    )
  }

  return (
    <NotificationContext.Provider value={wrappedValue}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useAppNotifications(): UseNotificationsReturn {
  const ctx = useContext(NotificationContext)
  if (!ctx)
    throw new Error(
      'useAppNotifications must be used within NotificationProvider',
    )
  return ctx
}
